import { Box, Button, CloseButton, Popover, SidePanel, Text } from '@wix/design-system';
import React from 'react';

import { RicosContext } from './../../ricos-context';

import {
  AI_IMAGE_MODAL_CANCEL_BUTTON,
  AI_IMAGE_MODAL_ERROR_CANCEL_BUTTON,
  AI_IMAGE_MODAL_ERROR_RETRY_BUTTON,
  AI_IMAGE_MODAL_PROMPT_HELPER_POPOVER,
  AI_IMAGE_MODAL_PROMPT_HELPER_POPOVER_CLOSE,
  AI_IMAGE_MODAL_PROMPT_INPUT,
  AI_IMAGE_MODAL_SUBMIT_BUTTON,
} from '../data-hooks';
import type { PromptInputAreaRef } from '../prompt-input-area/prompt-input-area';
import { PromptInputArea } from '../prompt-input-area/prompt-input-area';
import { isOfType } from '../ts-utils';
import styles from './ai-image-modal-scss';
import { ImageStyleSelector } from './image-style-selector';
import type { AiImageModalState, ImageStyle } from './slice/types';

const PROMPT_CHAR_LIMIT = 500;

type FooterProps = {
  modalRef: HTMLDivElement | null;
  zIndex: number;
  state: AiImageModalState;
  goBack: () => void;
  retryPrompt: () => void;
  isConvertedPromptHelperVisible: boolean;
  closeOptimisedPromptHelper: () => void;
  setPromptValue: (value: string) => void;
  setImageStyleValue: (value: ImageStyle) => void;
  cancelPrompt: () => void;
  submitPrompt: (
    prompt: string,
    imageStyle: ImageStyle,
    options: { isDirty: boolean; isRetry?: boolean }
  ) => void;
  publishModalAction: (action: string, value?: string, imageStyle?: string) => void;
};

export type FooterRef = {
  focus: () => void;
};

export const Footer = React.forwardRef<FooterRef, FooterProps>(
  (
    {
      modalRef,
      zIndex,
      state,
      goBack,
      retryPrompt,
      isConvertedPromptHelperVisible,
      setPromptValue,
      setImageStyleValue,
      cancelPrompt,
      closeOptimisedPromptHelper,
      submitPrompt,
      publishModalAction,
    },
    ref
  ) => {
    const { t } = React.useContext(RicosContext);
    const inputRef = React.useRef<PromptInputAreaRef>(null);

    React.useImperativeHandle(
      ref,
      () => {
        return {
          focus() {
            inputRef.current?.focus();
          },
        };
      },
      []
    );

    if (!modalRef) {
      return null;
    }

    return (
      <SidePanel.Footer noPadding showDivider={!isOfType(['INITIAL', 'INITIALIZING'], state)}>
        <Box
          paddingTop="SP3"
          paddingBottom="SP2"
          paddingLeft="SP4"
          paddingRight="SP4"
          align="right"
          className={styles.footer}
        >
          {(() => {
            switch (state.type) {
              case 'ERROR': {
                return (
                  <Box gap="SP2" paddingBottom="SP1">
                    <Button
                      dataHook={AI_IMAGE_MODAL_ERROR_CANCEL_BUTTON}
                      size="small"
                      priority="secondary"
                      onClick={() => {
                        goBack();
                        publishModalAction('back_from_error');
                      }}
                    >
                      {t('AIPlugin_Error_Button_GoBack')}
                    </Button>
                    <Button
                      dataHook={AI_IMAGE_MODAL_ERROR_RETRY_BUTTON}
                      size="small"
                      priority="primary"
                      onClick={() => {
                        retryPrompt();
                        publishModalAction('retry_from_error');
                      }}
                    >
                      {t('AIPlugin_Error_Button_Retry')}
                    </Button>
                  </Box>
                );
              }
              default: {
                return (
                  <Box direction="vertical" gap="SP2" width="100%">
                    <Popover
                      dataHook={AI_IMAGE_MODAL_PROMPT_HELPER_POPOVER}
                      fixed
                      shown={isConvertedPromptHelperVisible}
                      appendTo={modalRef}
                      placement="top-start"
                      theme="dark"
                      zIndex={zIndex}
                      showArrow
                    >
                      <Popover.Element>
                        <PromptInputArea
                          minRowsAutoGrow={2}
                          maxHeight="48px"
                          dataHook={AI_IMAGE_MODAL_PROMPT_INPUT}
                          readonly={isOfType(['IN_PROGRESS'], state)}
                          busy={isOfType(['INITIALIZING'], state)}
                          value={state.type === 'INITIALIZING' ? '' : state.textualPrompt}
                          busyValue={
                            state.type === 'INITIALIZING'
                              ? t('AIPlugin_Loading_Initialising')
                              : state.textualPrompt
                          }
                          onChange={setPromptValue}
                          onSubmit={() => {
                            if (!isOfType(['INITIALIZING', 'IN_PROGRESS'], state)) {
                              submitPrompt(state.textualPrompt, state.imageStyle, {
                                isDirty: state.isDirty,
                              });
                            }
                          }}
                          onCancel={() => {
                            cancelPrompt();
                          }}
                          ref={inputRef}
                          maxLength={PROMPT_CHAR_LIMIT}
                          zIndex={zIndex}
                          placeholder={t('AIPlugin_Input_ImageGeneratePlaceholder_Text')}
                        />
                      </Popover.Element>
                      <Popover.Content>
                        <CloseButton
                          dataHook={AI_IMAGE_MODAL_PROMPT_HELPER_POPOVER_CLOSE}
                          className={styles.closeButton}
                          onClick={closeOptimisedPromptHelper}
                          skin="light"
                          size="medium"
                        />
                        <Box
                          paddingTop="SP3"
                          paddingBottom="SP3"
                          paddingLeft="SP4"
                          paddingRight="SP4"
                          direction="vertical"
                          maxWidth="380px"
                          gap="SP2"
                        >
                          <Text size="small" light weight="thin">
                            {t('AIPlugin_Helper_GenerateImage_Optimise')}
                          </Text>
                          <Box>
                            <Button
                              skin="light"
                              size="small"
                              priority="secondary"
                              onClick={closeOptimisedPromptHelper}
                            >
                              {t('AIPlugin_Helper_GenerateImage_Button')}
                            </Button>
                          </Box>
                        </Box>
                      </Popover.Content>
                    </Popover>

                    <Box direction="horizontal" align="space-between" verticalAlign="middle">
                      <ImageStyleSelector
                        disabled={isOfType(['IN_PROGRESS', 'INITIALIZING'], state)}
                        value={state.imageStyle}
                        onChange={setImageStyleValue}
                        zIndex={zIndex}
                        modalRef={modalRef}
                      />
                      <Button
                        size="small"
                        disabled={state.type === 'INITIALIZING' || state.textualPrompt.length === 0}
                        dataHook={
                          state.type === 'IN_PROGRESS'
                            ? AI_IMAGE_MODAL_CANCEL_BUTTON
                            : AI_IMAGE_MODAL_SUBMIT_BUTTON
                        }
                        priority={state.type === 'IN_PROGRESS' ? 'secondary' : 'primary'}
                        onClick={() => {
                          if (state.type === 'INITIALIZING') {
                            return;
                          }
                          if (state.type === 'IN_PROGRESS') {
                            cancelPrompt();
                            return;
                          }
                          submitPrompt(state.textualPrompt, state.imageStyle, {
                            isDirty: state.isDirty,
                          });
                        }}
                      >
                        {t(
                          state.type === 'IN_PROGRESS'
                            ? 'AIPlugin_Action_StopGenerating'
                            : isOfType(['INITIAL', 'INITIALIZING'], state)
                            ? 'AIPlugin_Action_Generate'
                            : 'AIPlugin_Action_Regenerate'
                        )}
                      </Button>
                    </Box>
                  </Box>
                );
              }
            }
          })()}
        </Box>
      </SidePanel.Footer>
    );
  }
);
